<template>
  <div class="model-sections">
    <div lines="none" v-for="(thisElement, eIndex) in carouselMessage?.template?.elements || adMessageJsonElements" :key="eIndex"
      class="table-reorder ion-no-padding rowMargin">
      <!-- text -->
      <div class="element-container" v-if="thisElement.type === 'text' || thisElement.type === 'reply'">
        <div v-if="selectedChannel !== 'googlercs'">
          <!-- SMS Parts Display -->
          <div v-if="selectedChannel === 'sms' && smsStats.parts.length > 0" class="sms-parts-container">
            <div v-for="(part, partIndex) in smsStats.parts" :key="'part-' + partIndex" class="sms-part-wrapper">
              <RichTextBoxV2
                :ref="'TextRichTextBox-' + partIndex"
                :value="part"
                :showSmiley="false"
                :showCustomFiledInsertion="false"
                :maxLength="0"
                :readonly="true"
                :showRemainingText="false"
                :showBotVariable="false"
              />
            </div>
          </div>

          <!-- Single message display for non-SMS -->
          <RichTextBoxV2
            v-else
            ref="TextRichTextBox"
            :value="thisElement.payload"
            :showSmiley="false"
            :showCustomFiledInsertion="false"
            :maxLength="0"
            :readonly="true"
            :showRemainingText="false"
            :showBotVariable="false"
          />

          <div v-if="thisElement.buttons && thisElement.buttons.length">
            <div v-for="(thisButton, bIndex) in thisElement.buttons" :key="'element' + eIndex + 'button' + bIndex" @click="onclickButton('text', bIndex, eIndex)" class="bttnContainer cursorPointer" :class="{ 'lastButton' : bIndex == 2}" :style="{ justifyContent: 'center' }">
              <div class="button-text-container">
                <span class="bttnTitle ion-no-margin">{{ thisButton.text }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <RcsPreview 
            :contentType="thisElement.type" 
            :messageText="thisElement.payload"
            :mediaSource="thisElement.payload"
            :quickreplyButtons="thisElement.buttons"
            :finalUrlType="finalUrlType"
          />
        </div>
      </div>
      <!-- image & video -->
      <div v-if="thisElement.type === 'image' || thisElement.type === 'video' || thisElement.type === 'document'">
        <RcsPreview 
          :contentType="thisElement.type" 
          :messageText="thisElement.payload"
          :mediaSource="thisElement"
          :finalUrlType="finalUrlType"
        />
      </div>
      <!-- Google RCS Carousel -->
      <div v-if="thisElement.type === 'carousel' || thisElement.type === 'richcard'">  
        <RcsPreview 
          :contentType="thisElement.type" 
          :carouselMessage="carouselMessage"
          :finalUrlType="finalUrlType"
        />
      </div>
    </div>
     <!-- FallBack Message -->
    <div v-if="fallBackMessage !== '' && checkFallBackMessageEnabled" class="custBorderTop">
      <p class="ion-no-margin ion-padding-bottom sctn-labels">Fallback message</p>
      <div class="fallBackContainer" v-html="formattedFallBackMessage"> </div>
    </div>

    <!-- SMS Statistics Card -->
    <div v-if="selectedChannel === 'sms' && smsStats.characterCount > 0" class="sms-stats-card">
      <div class="sms-stats-content">
        <div class="stat-item">
          <span class="stat-label">Characters:</span>
          <span class="stat-value">{{ smsStats.characterCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Estimated Message Parts:</span>
          <span class="stat-value">{{ smsStats.messagePartCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Unicode Required:</span>
          <span class="stat-value">
            <VIcon 
              name="smsStats.requiresUnicode ? check-circle-line : close-circle-line" 
              :class="smsStats.requiresUnicode ? 'unicode-yes' : 'unicode-no'"
            ></VIcon>
            {{ smsStats.requiresUnicode ? 'Yes' : 'No' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { IonTextarea } from "@ionic/vue";
import RcsPreview from '@/components/rcsPreview.vue';
import { getIcon } from '@/helpers/icon.helper';
import RichTextBoxV2 from '@/components/rich-text-box-v2.vue';
import { calculateSMSStats } from '@/helpers/sms.helper';

const videocamOutline = 'video-line';
const imageOutline = 'image-line';
const documentOutline = 'clear-file-line';
const statsChartOutline = 'chart-line';

export default {
  components: { IonTextarea, RcsPreview, RichTextBoxV2 },
  mixins: [],
  data() {
    return {
      imageOutline,
      videocamOutline,
      documentOutline,
      statsChartOutline,
      isEnablePrevButton: false,
      isEnableNextButton: true
    }
  },
  props: {
    adMessageJsonElements: {
      type: Array,
      default: () => { return []; }
    },
    carouselMessage: {
      type: Array,
      default: () => { return []; }
    },
    fallBackMessage: {
      type: String,
      default: ""
    },
    checkFallBackMessageEnabled: {
      type: Boolean,
      default: false
    },
    selectedChannel: {
      type: String,
      default: ""
    },
    isViewOnlyMode: {
      type: Boolean,
      default: false
    },
    finalUrlType: {
      type: String,
      default: "static"
    }
  },
  computed: {
    formattedFallBackMessage() {
      return this.fallBackMessage.replace(/\n/g, '<br>');
    },
    smsStats() {
      // Calculate SMS statistics only for SMS channel
      if (this.selectedChannel !== 'sms') {
        return {
          characterCount: 0,
          messagePartCount: 0,
          requiresUnicode: false,
          parts: []
        };
      }

      // Get the text message from adMessageJsonElements
      const textElement = (this.carouselMessage?.template?.elements || this.adMessageJsonElements || [])
        .find(el => el.type === 'text' || el.type === 'reply');
      
      if (!textElement || !textElement.payload) {
        return {
          characterCount: 0,
          messagePartCount: 0,
          requiresUnicode: false,
          parts: []
        };
      }

      // Calculate stats using helper
      return calculateSMSStats(textElement.payload);
    }
  },
  methods: {
    getIconType(type) {
      switch (type) {
        case 'image':
          return imageOutline;
        case 'video':
          return videocamOutline;
        default:
          return documentOutline;
      }
    },
    getIconForButton(button) {
      return getIcon(button);
    }
  }
}
</script>

<style scoped>
.element-container{
  width: 100%;
}
.paddingLeft {
  padding-left: 10px;
}
.img-container {
  width: 50%;
  display: block;
  margin: 20px auto 20px auto;
}
.textarea-box, .reply_textarea-box {
  width: 100%;
  height: auto;
  background: var(--background-primary);
  display: inline-block;
}
.reply_textarea-box{
  border: 1px solid var(--gray-04);
  border-radius: 5px;
}
.img-block{
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border: 1px var(--gray-04) solid;
  border-radius: 5px;
  width: 100%;
  cursor: pointer;
  background: var(--background-primary);
}
.img-block video, .img-block img {
  border-radius: 5px;
}
.jinput {
  background-color: var(--white);
}
.quick-reply-button-container {
  display: flex;
  column-gap: 10px;
  max-width: 100%;
  overflow-x: scroll;
}

.quick-reply-button-container::-webkit-scrollbar {
  height: 5px;
  width: 5px;
  background-color: var(--white);
}
.quick-reply-buttons-wrapper {
  display: flex;
  column-gap: 10px;
}
.quick-reply-buttons-wrapper .btn-template-dynamic-action {
  cursor: pointer;
  max-width: 180px;
  width: 180px !important;
  height: 30px;
  --background: var(--white);
  color: var(--purple);
  --border-radius: 15px !important;
  --border-width: 1px;
  --border-color: var(--purple);
  margin: 10px 0px;
}
.quick-reply-button-container::-webkit-scrollbar-thumb{
  background-color: var(--purple); 
  background-image: -webkit-linear-gradient(45deg,
    rgba(255, 255, 255, .2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, .2) 50%,
    rgba(255, 255, 255, .2) 75%,
    transparent 75%,
    transparent);
  -webkit-border-radius: 5px;
  border-radius: 5px;
}
.bttnContainer{
	background: var(--white);
	border: 1px solid var(--purple);
	height: 50px;
	display: flex;
	align-items: center;
}
.bttnContainer:last-child{
	border-radius: 0px 0px 5px 5px;
	padding-bottom: 7px;
	height: 57px;
}
.bttnContainer:not(:last-child) {
  border-bottom: none;
}
.bttnTitle{
  color: var(--purple);
  font-size: 14px;
}
.replyBox{
  border: 1px solid var(--gray-04);
  border-bottom: none;
  border-radius: 5px 5px 0px 0px;
}
.templateInputWithoutBttn{
  border-bottom: 1px solid var(--black) !important;
  border-radius: 5px;
}
.title {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: var(--color);
}
.flex-container{
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.product-block{
  border-radius: 5px 5px 0px 0px;
  border: 1px var(--gray-04) solid;
  border-bottom: none;
  cursor: pointer;
  background: var(--white);
}
.products-container{
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 200px;
  display: block;
  margin: 20px auto 20px auto;
}
.carouselContainer{
  width: 100%;
  display: inline-block;
}
.column-container{
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 20px 0px 20px;
  padding-bottom: 20px;
}
.custom-upload-container{
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-primary);
}
.custBorderTop{
  border-top: 1px solid #EDEDED;
  padding-top: 20px;
  margin-top: 20px;
}
.fallBackContainer{
  background: #EDEDED;
  padding: 15px 10px;
  border-radius: 5px;
  color: #242424;
  line-height: 30px;
}

/* SMS Parts Styles */
.sms-parts-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 10px;
}

.sms-part-wrapper {
  position: relative;
  padding-top: 8px;
}

/* SMS Statistics Card */
.sms-stats-card {
  margin-top: 20px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  /* background: var(--background-primary); */
  overflow: hidden;
}

.sms-stats-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  font-size: 14px;
  font-weight: 500;
}

.stat-value {
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.unicode-yes {
  color: #10b981;
  font-size: 18px;
}

.unicode-no {
  color: #6b7280;
  font-size: 18px;
}

@media only screen and (max-width: 760px){
  .carouselContainer{
    width: 95%;
    display: inline-block;
  }
  
  .sms-stats-content {
    padding: 12px;
    gap: 10px;
  }
  
  .stat-item {
    padding: 6px 10px;
  }
  
  .stat-label {
    font-size: 12px;
  }
  
  .stat-value {
    font-size: 13px;
  }
}
</style>